import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  Restaurant as RestaurantIcon,
  AutoAwesome as AIIcon,
  Schedule as ScheduleIcon,
  ShoppingCart as CartIcon,
} from "@mui/icons-material";
import { MealPlanGenerator } from "../components/MealPlanning";
import { colors } from "../theme";

const Wrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    ${colors.background.light} 0%,
    ${colors.background.paper} 100%
  );
  padding: 20px 0;
`;

const HeaderSection = styled(Box)`
  background: ${colors.gradients.secondary};
  color: white;
  padding: 40px 0;
  margin-bottom: 30px;
  text-align: center;
`;

const HeaderTitle = styled(Typography)`
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
  font-family: "Montserrat", sans-serif;
`;

const HeaderSubtitle = styled(Typography)`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const FeatureCard = styled(Card)`
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${colors.gradients.primary};
  color: white;
  margin: 0 auto 16px;
`;

const StepsContainer = styled(Paper)`
  padding: 32px;
  margin: 32px 0;
  border-radius: 16px;
  background: white;
`;

const steps = [
  "Set Your Preferences",
  "AI Generates Your Plan",
  "Review & Customize",
  "Schedule Your Meals",
  "Get Your Grocery List",
];

export const MakeAPlan = observer(() => {
  return (
    <Wrapper>
      <HeaderSection>
        <Container maxWidth="lg">
          <HeaderTitle variant="h1">AI-Powered Meal Planning</HeaderTitle>
          <HeaderSubtitle variant="subtitle1">
            Let our intelligent system create personalized meal plans based on
            your preferences, dietary goals, and nutritional needs. From
            planning to grocery shopping, we've got you covered.
          </HeaderSubtitle>
        </Container>
      </HeaderSection>

      <Container maxWidth="lg">
        {/* How It Works Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            align="center"
            sx={{ mb: 4, fontWeight: "bold", fontFamily: "Montserrat" }}
          >
            How It Works
          </Typography>

          <StepsContainer elevation={2}>
            <Stepper alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepsContainer>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={12} md={3}>
            <FeatureCard>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <FeatureIcon>
                  <AIIcon fontSize="large" />
                </FeatureIcon>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  AI-Powered
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Our AI analyzes your preferences, allergies, and goals to
                  create perfectly tailored meal plans.
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <FeatureCard>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <FeatureIcon>
                  <RestaurantIcon fontSize="large" />
                </FeatureIcon>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Personalized Recipes
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Get recipes that match your taste preferences and dietary
                  restrictions perfectly.
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <FeatureCard>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <FeatureIcon>
                  <ScheduleIcon fontSize="large" />
                </FeatureIcon>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Smart Scheduling
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Automatically schedule meals in your calendar with prep time
                  and cooking reminders.
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <FeatureCard>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <FeatureIcon>
                  <CartIcon fontSize="large" />
                </FeatureIcon>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Grocery Integration
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Automatically generate shopping lists and integrate with your
                  favorite grocery stores.
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>
        </Grid>

        {/* Meal Plan Generator */}
        <MealPlanGenerator />

        {/* Call to Action */}
        <Box sx={{ textAlign: "center", mt: 6, mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            Ready to Transform Your Meal Planning?
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
          >
            Join thousands of users who have revolutionized their cooking and
            nutrition with our AI-powered meal planning system.
          </Typography>
        </Box>
      </Container>
    </Wrapper>
  );
});
